import usePropertyService from 'api/services/propertyService'
import {
  buildAccountPath,
  buildVerifyEmailPath,
  markListingOwnerIntent,
} from 'api/utils/pendingCreateAdFlow'
import { canSubmitListingVerification } from 'components/myAccount/mySpaceUtils'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { VerificationStatus } from 'types/property'

type CreateAdPublishSuccessProps = {
  listingId: string
  mode: 'created' | 'updated'
}

type ListingMetaLike = { status?: string | null } | null | undefined

const ReviewShieldIcon = () => (
  <div
    className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#DCE8F8]"
    aria-hidden
  >
    <svg viewBox="0 0 48 48" className="h-12 w-12 text-imovPrimary" fill="none">
      <path
        d="M24 6.5c6.2 3.4 10.8 4.2 14.5 4.4v12.3c0 8.6-5.6 14.8-14.5 18.3C15.1 38 9.5 31.8 9.5 23.2V10.9c3.7-.2 8.3-1 14.5-4.4Z"
        fill="currentColor"
        fillOpacity="0.15"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinejoin="round"
      />
      <path
        d="M24 30.5c-3.4-2.5-5.6-4.4-5.6-7 0-1.7 1.2-2.9 2.8-2.9 1 0 1.9.5 2.8 1.5.9-1 1.8-1.5 2.8-1.5 1.6 0 2.8 1.2 2.8 2.9 0 2.6-2.2 4.5-5.6 7Z"
        fill="currentColor"
      />
    </svg>
  </div>
)

function listingMetaStatus(listing: {
  listingMeta?: ListingMetaLike
  listing_meta?: ListingMetaLike
} | null | undefined): string {
  const meta = listing?.listingMeta ?? listing?.listing_meta
  return typeof meta?.status === 'string' ? meta.status.trim() : ''
}

/** Support-approved / publicly live — edits do not need docs or re-review. */
function isListingAlreadyValidated(
  verified: VerificationStatus | null | undefined,
  status: string
): boolean {
  if (verified === VerificationStatus.Approved) return true
  return status === 'active'
}

const CreateAdPublishSuccess = ({ listingId, mode }: CreateAdPublishSuccessProps) => {
  const { t } = useTranslation()
  const { emailValidated, refresh } = usePermissions()
  const { loadMyPropertyById } = usePropertyService()
  const isUpdated = mode === 'updated'
  const accountWithListing = buildAccountPath(listingId)
  const managePath = `/my-listings/${listingId}/manage`

  const { data: listing, isFetched } = useQuery(
    ['publishSuccessListing', listingId],
    () => loadMyPropertyById(listingId),
    {
      enabled: Boolean(listingId),
      staleTime: 0,
      cacheTime: 0,
    }
  )

  const verified = listing?.verified as VerificationStatus | null | undefined
  const metaStatus = listingMetaStatus(listing)
  const alreadyValidated = isListingAlreadyValidated(verified, metaStatus)
  const docsAlreadySubmitted =
    verified === VerificationStatus.Pending ||
    metaStatus === 'property_docs_in_review' ||
    metaStatus === 'ready_to_publish'

  // New listings need docs. Edits of validated/in-review listings do not.
  const needsDocuments = !isUpdated
    ? true
    : isFetched
      ? !alreadyValidated &&
        !docsAlreadySubmitted &&
        canSubmitListingVerification(verified ?? null)
      : false

  const primaryTo = !emailValidated
    ? buildVerifyEmailPath(accountWithListing)
    : needsDocuments
      ? managePath
      : accountWithListing

  const primaryLabel = !emailValidated
    ? t('createAd.flow.success.verifyEmailCta')
    : needsDocuments
      ? t('createAd.flow.success.uploadDocumentsCta')
      : t('createAd.flow.success.dashboardCta')

  useEffect(() => {
    markListingOwnerIntent()
    void refresh({ intent: 'list_property' })
  }, [listingId, refresh])

  const showActionBox = !emailValidated || needsDocuments
  const showAwaitingBadge = !alreadyValidated && (needsDocuments || docsAlreadySubmitted)

  const subtitle = (() => {
    if (!isUpdated) return t('createAd.flow.success.revisionSubtitle')
    if (alreadyValidated) return t('createAd.flow.success.updatedSubtitleLive')
    if (needsDocuments) return t('createAd.flow.success.updatedSubtitle')
    return t('createAd.flow.success.updatedSubtitleDocsDone')
  })()

  return (
    <div className="mx-auto flex min-h-[55vh] max-w-2xl flex-col items-center justify-center px-4 py-10 text-center sm:py-14">
      <ReviewShieldIcon />

      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        {alreadyValidated ? (
          <p className="inline-flex items-center rounded-full bg-emerald-600 px-3.5 py-1.5 text-xs font-semibold text-white">
            {t('createAd.flow.success.liveBadge')}
          </p>
        ) : null}
        {showAwaitingBadge ? (
          <p className="inline-flex items-center rounded-full bg-imovPrimary px-3.5 py-1.5 text-xs font-semibold text-white">
            {t('createAd.flow.success.awaitingValidationBadge')}
          </p>
        ) : null}
        {showActionBox ? (
          <p className="inline-flex items-center rounded-full bg-amber-100 px-3.5 py-1.5 text-xs font-semibold text-amber-900 ring-1 ring-amber-200">
            {t('createAd.flow.success.actionRequiredBadge')}
          </p>
        ) : null}
      </div>

      <h2 className="mt-6 text-2xl font-bold tracking-tight text-imovSecondary sm:text-[1.75rem] sm:leading-snug">
        {isUpdated
          ? t('createAd.flow.success.updatedTitle')
          : t('createAd.flow.success.revisionTitle')}
      </h2>

      <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-gray-500 sm:text-[15px]">
        {subtitle}
      </p>

      {showActionBox ? (
        <div className="mx-auto mt-7 w-full max-w-lg rounded-2xl border border-amber-200 bg-amber-50/80 px-5 py-5 text-left">
          <p className="text-sm font-bold text-imovSecondary">
            {t('createAd.flow.success.actionRequiredTitle')}
          </p>
          <ol className="mt-3 space-y-3 text-sm leading-relaxed text-gray-700">
            {!emailValidated ? (
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-imovPrimary text-xs font-bold text-white">
                  1
                </span>
                <span>{t('createAd.flow.success.actionRequiredEmail')}</span>
              </li>
            ) : null}
            {needsDocuments ? (
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-imovPrimary text-xs font-bold text-white">
                  {emailValidated ? '1' : '2'}
                </span>
                <span>{t('createAd.flow.success.actionRequiredDocuments')}</span>
              </li>
            ) : null}
          </ol>
        </div>
      ) : null}

      <div className="mt-8 flex w-full max-w-lg flex-col items-stretch justify-center">
        <Link
          to={primaryTo}
          className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-imovPrimary px-5 py-3 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
        >
          {primaryLabel}
        </Link>
      </div>
    </div>
  )
}

export default CreateAdPublishSuccess
